import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, Bell, ShoppingCart, MoreVertical } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import NotificationsDropdown from "@/components/layout/NotificationsDropdown";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import STXThemeToggle from "@/components/stx/STXThemeToggle";
import Brand from "@/components/common/Brand";
import HeaderMoreMenu from "@/components/layout/HeaderMoreMenu";

export default function Header() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // iter79 Batch 2: Kontextabhängige Suche — je nach aktuellem Bereich wird
  // die Header-Lupe in eine gefilterte Suche geleitet.
  const searchScope = (() => {
    const p = location.pathname;
    if (p.startsWith("/marketplace")) return "MARKETPLACE";
    if (p.startsWith("/stilltouchedx") || p.startsWith("/stx")) return "STILLTOUCHEDX";
    return "ALL";
  })();
  const searchPath = searchScope === "ALL" ? "/search" : `/search?scope=${searchScope}`;

  // iter154 · Zentrales Bereichs-Label im Header (Social/MP/STX) + Dark-Toggle für STX-Profile.
  // Detektiert die aktuelle Profil-Ansicht via URL-Pfad.
  const areaLabel = (() => {
    const p = location.pathname;
    // /user/{handle}/marketplace  → MARKETPLACE
    // /user/{handle}/stx          → STILLTOUCHEDX
    // /user/{handle}(/social)?    → SOCIAL
    if (/^\/user\/[^/]+\/stx(\/|$)/.test(p) || /^\/user\/[^/]+\/stilltouchedx(\/|$)/.test(p)) return "STILLTOUCHEDX";
    if (/^\/user\/[^/]+\/marketplace(\/|$)/.test(p)) return "MARKETPLACE";
    if (/^\/user\/[^/]+(\/social)?$/.test(p)) return "SOCIAL";
    return null;
  })();
  const areaText = areaLabel === "STILLTOUCHEDX"
    ? "stilltouchedX"
    : areaLabel === "MARKETPLACE"
    ? "Marketplace"
    : areaLabel === "SOCIAL"
    ? "Social"
    : null;

  useEffect(() => {
    if (!user || user === false) return;
    const load = async () => {
      try {
        const { data } = await api.get("/me/watch-notifications/summary");
        setUnreadCount(data.total || 0);
      } catch { /* ignore */ }
      try {
        const { data } = await api.get("/cart/summary");
        setCartCount(data.count || 0);
      } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 30000);
    const onRefresh = () => load();
    window.addEventListener("watch-unread-refresh", onRefresh);
    window.addEventListener("cart-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("watch-unread-refresh", onRefresh);
      window.removeEventListener("cart-refresh", onRefresh);
    };
  }, [user]);

  return (
    <header data-testid="main-header"
      className="sticky top-0 z-50 bg-[#0A0A0A] border-b border-[#222]">
      <div className="relative flex items-center justify-between px-4 sm:px-6 md:px-8 h-14 w-full max-w-7xl mx-auto">
      <button data-testid="brand-logo" onClick={() => navigate("/")}
        className="active:scale-[0.98] transition-transform">
        <Brand className="text-xl tracking-tight" />
      </button>

      {/* iter154/iter161 · Zentriertes Bereichs-Label bei Profil-Ansichten (Social/MP/STX).
          iter161: Auf Mobile komplett ausgeblendet (überlagerte sich mit DE/Plus/Search etc.).
                   STX Light/Dark-Toggle wandert für Mobile ins Profil-Menü.
          Ab md sichtbar. */}
      {areaText && (
        <div
          data-testid={`header-area-label-${areaLabel.toLowerCase()}`}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:flex items-center gap-2 pointer-events-none"
        >
          <span
            className={`inline-flex items-center h-8 px-3 rounded-full text-[10px] font-semibold tracking-wider border ${
              areaLabel === "STILLTOUCHEDX"
                ? "border-stx-accent/50 text-stx-accent bg-black/40"
                : "border-white/25 text-white bg-white/5"
            }`}
          >
            {areaText}
          </span>
          {areaLabel === "STILLTOUCHEDX" && (
            <span className="pointer-events-auto inline-flex items-center">
              <STXThemeToggle />
            </span>
          )}
        </div>
      )}

      <div className="relative flex items-center gap-1">
        <LanguageSwitcher compact />
        {/* iter169 · Plus-Button (Composer) entfernt — Composer bereits inline oben im Feed. */}
        <button data-testid="header-search-btn" onClick={() => navigate(searchPath)}
          className="p-2 text-white/70 hover:text-white transition-colors"
          title={searchScope === "MARKETPLACE" ? t("header.search_mp") : searchScope === "STILLTOUCHEDX" ? t("header.search_stx") : t("header.search")}>
          <Search size={20} />
        </button>
        {user && user !== false && (
          <button
            data-testid="header-cart-btn"
            onClick={() => navigate("/cart")}
            aria-label={t("header.cart")}
            className="relative p-2 text-white/70 hover:text-white transition-colors">
            <ShoppingCart size={20} />
            {cartCount > 0 && (
              <span data-testid="header-cart-badge"
                className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-emerald-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {cartCount > 9 ? "9+" : cartCount}
              </span>
            )}
          </button>
        )}
        {user && user !== false && (
          <button
            data-testid="header-notifications-btn"
            onClick={() => setDropdownOpen(v => !v)}
            aria-label={t("header.notifications")}
            className="relative p-2 text-white/70 hover:text-white transition-colors">
            <Bell size={20} />
            {unreadCount > 0 && (
              <span data-testid="header-notifications-badge"
                className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
        )}
        {dropdownOpen && (
          <NotificationsDropdown
            onClose={() => setDropdownOpen(false)}
            unreadCount={unreadCount}
            setUnreadCount={setUnreadCount}
          />
        )}
        {/* iter169 · 3-Punkte-Menü rechts außen — kontext-sensitiv (News/FAQ je Bereich + Impressum/AGB/Datenschutz global) */}
        <HeaderMoreMenu />
      </div>
      </div>
    </header>
  );
}
